import callCmAPI from 'services/helpers/call-cm-api'
import { KnownSubscriptionPlan } from 'types'
import { AvailableCountriesCapital } from 'types'
import { shrinkAllowCreditPolicy } from 'utils/transform-legacy-rights'
/******************
 * Fetch from API *
 ******************/

type creditPolicyOptions =
  | 'exclude_risika_scores'
  | 'exclude_company_types'
  | 'exclude_level_of_auditor_assistances'
  | 'exclude_industries'
  | 'exclude_companies'
  | 'exclude_persons'
  | 'exclude_vat'
  | 'bankruptcy_limit'
  | 'age_limit'
  | 'negative_equity_limit'
  | 'prepaid_phone_number'
  | 'phone_number_registry'
  | 'distance_to_ceo'
  | 'bankruptcy_limit_long'
  | 'auditor_has_id'

export type CustomerRestrictionsResponse = {
  subscription_plan: KnownSubscriptionPlan
  max_users: number
  caps: {
    exclude_companies: number
    exclude_persons: number
    credit_ratings: number
    credit_reports: number
    lookups: number
    monitoring_count: number
    monitoring_exports: number
    search_exports: number
  }
  counters: {
    exclude_companies?: number
    exclude_persons?: number
    credit_ratings?: number
    credit_reports?: number
    lookups?: number
    monitoring_count?: number
    monitoring_exports?: number
    search_exports?: number
    // Unique Nordic companies looked up in the current period (ENG2-2459).
    // Absent until RisikaAPI ships it; the Usage table shows "Not tracked".
    nordic_lookups_used?: number
    // Current Nordic lookup contract year (ENG2-2461). Absent before the
    // RisikaAPI release that ships T6; null when there is no period.
    nordic_lookups_current_period_start?: string | null
    nordic_lookups_current_period_end?: string | null
  }
  rights: RightsType
}
type AvailableModels = 'BOHR' | 'NEWTON'
type ScoreModels = { model?: AvailableModels; explanation?: boolean }[]

export type CountrySpecificTypes = {
  [key in AvailableCountriesCapital]: { score_models: ScoreModels }
}
export type BeneficialOwnersLegitimateReason =
  | 'CDD'
  | 'SANCTIONS_SCREENING'
  | 'CREDIT_RISK'

export type BeneficialOwnersType = {
  enabled: boolean
  default_legitimate_reason: BeneficialOwnersLegitimateReason | null
}

export interface RightsType extends CountrySpecificTypes {
  global: {
    observational_lists: boolean
    credit_override: boolean
    fraud_indicators: boolean
    global_data: boolean
    beneficial_owners: BeneficialOwnersType
    // Contract-level global data subscription (ENG2-2391). The start date is
    // informational; a null end date means the contract auto-renews.
    global_data_subscription_start_date: string | null
    global_data_subscription_end_date: string | null
    // Risika for AI end date (ENG2-2614): the API switches allow_ai off after
    // it; null means no end. Absent before RisikaAPI ENG2-2611 ships.
    ai_end_date?: string | null
    // Start of the Nordic lookup contract year (ENG2-2459 / ENG2-2461). Only
    // sent by RisikaAPI from the release that ships T6.
    nordic_subscription_start_date?: string | null
  }
  allow_api: boolean
  allow_ai: boolean
  allow_basic: boolean
  allow_browser_extension: boolean
  allow_company_export: boolean
  allow_company_search: boolean
  allow_countries: AvailableCountriesCapital[]
  allow_credit: boolean
  allow_credit_policy: creditPolicyOptions[]
  allow_ean: boolean
  allow_financial: boolean
  allow_hierarchy: boolean
  allow_highlights: boolean
  allow_monitoring: boolean
  allow_monitoring_export: boolean
  allow_notes: boolean
  allow_pdf_reports: boolean
  allow_person_export: boolean
  allow_person_search: boolean
  allow_relations: boolean
  allow_sales_triggers: boolean
  allow_zones: Record<string, string[]> | null
  search_exports_limit: number
  tier: 1 | 2 | 3 | 4
}

export type CountersType = {
  credit_ratings?: number
  credit_reports?: number
  exclude_companies?: number
  exclude_persons?: number
  lookups?: number
  monitoring_count?: number
  monitoring_exports?: number
  search_exports?: number
}
export type CapsType = {
  credit_ratings: number
  credit_reports: number
  exclude_companies: number
  exclude_persons: number
  lookups: number
  monitoring_count: number
  monitoring_exports: number
  search_exports: number
}

export interface CapsWithExtras extends CapsType {
  max_users: number
  search_exports_limit: number
}
export interface CountersWithExtras extends CountersType {
  max_users: number
  search_exports_limit: number
}

export type CountersCapsType = {
  counters: CountersType
  caps: CapsType
}

export type CountersCapsTypeWithExtras = {
  counters: CountersWithExtras
  caps: CapsWithExtras
}

export const getCustomer = (
  companyId: number,
): Promise<CustomerRestrictionsResponse> =>
  callCmAPI(`/customer/plan/${companyId}`, {
    method: 'get',
  })
    .then(({ data: { data } }) => {
      return {
        // special
        subscription_plan: data.subscription_plan,
        max_users: data.max_users,
        // caps
        caps: {
          exclude_companies: data.caps.credit_policy.exclude_companies,
          exclude_persons: data.caps.credit_policy.exclude_persons,
          credit_ratings: data.caps.credit_ratings,
          credit_reports: data.caps.credit_reports,
          lookups: data.caps.lookups,
          monitoring_count: data.caps.monitoring_count,
          monitoring_exports: data.caps.monitoring_exports,
          search_exports: data.caps.search_exports,
        },
        // counters
        counters: {
          exclude_companies: data.counters.credit_policy.exclude_companies,
          exclude_persons: data.counters.credit_policy.exclude_persons,
          credit_ratings: data.counters.credit_ratings,
          credit_reports: data.counters.credit_reports,
          lookups: data.counters.lookups,
          monitoring_count: data.counters.monitoring_count,
          monitoring_exports: data.counters.monitoring_exports,
          search_exports: data.counters.search_exports,
          number_of_global_lookups: data.counters.number_of_global_lookups,
          global_lookups_limit: data.counters.global_lookups_limit,
          global_lookups: data.counters.global_lookups,
          nordic_lookups_used: data.counters.nordic_lookups_used,
          // ENG2-2461: the current contract year. Absent (undefined) before the
          // RisikaAPI release that ships T6; null when the API has no period.
          nordic_lookups_current_period_start:
            data.counters.nordic_lookups_current_period_start,
          nordic_lookups_current_period_end:
            data.counters.nordic_lookups_current_period_end,
        },
        rights: {
          ...data.rights,
          ...{
            ...data.legacy_rights,
            allow_credit_policy: shrinkAllowCreditPolicy(
              data.legacy_rights.allow_credit_policy,
            ),
          },
        },
      }
    })
    .catch((err) => err)
